import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Plus, Search, ChevronRight, Copy, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CONFIG = {
  nao_enviado: { label: 'Não enviado', color: 'bg-zinc-700/50 text-zinc-300', dot: 'bg-zinc-500',
    btn: 'bg-zinc-700 border-zinc-900 shadow-[0_4px_0_rgb(24,24,27)] hover:bg-zinc-600' },
  enviado: { label: 'Enviado', color: 'bg-blue-500/20 text-blue-400', dot: 'bg-blue-500',
    btn: 'bg-blue-600 border-blue-800 shadow-[0_4px_0_rgb(30,64,175)] hover:bg-blue-500' },
  aprovado: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400', dot: 'bg-green-500',
    btn: 'bg-green-600 border-green-800 shadow-[0_4px_0_rgb(21,128,61)] hover:bg-green-500' },
  em_andamento: { label: 'Em andamento', color: 'bg-yellow-500/20 text-yellow-400', dot: 'bg-yellow-500',
    btn: 'bg-yellow-600 border-yellow-800 shadow-[0_4px_0_rgb(133,77,14)] hover:bg-yellow-500' },
  concluido: { label: 'Concluído', color: 'bg-purple-500/20 text-purple-400', dot: 'bg-purple-500',
    btn: 'bg-purple-600 border-purple-800 shadow-[0_4px_0_rgb(88,28,135)] hover:bg-purple-500' },
  pago: { label: 'Pago', color: 'bg-emerald-500/20 text-emerald-400', dot: 'bg-emerald-500',
    btn: 'bg-emerald-600 border-emerald-800 shadow-[0_4px_0_rgb(6,78,59)] hover:bg-emerald-500' },
};

const STATUS_ORDER = ['concluido', 'em_andamento', 'aprovado', 'enviado', 'nao_enviado', 'pago'];

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

export default function Home() {
  const navigate = useNavigate();
  const { canCreate } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { loadProposals(); }, []);

  const loadProposals = async () => {
    try {
      const res = await api.get('/proposals/');
      setProposals(res.data);
    } catch (err) {
      toast.error('Erro ao carregar orçamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleNewProposal = () => {
    if (!canCreate()) {
      toast.error('Assine um plano para criar novos orçamentos');
      navigate('/subscribe');
      return;
    }
    navigate('/proposals/new');
  };

  const duplicateProposal = async (e, id) => {
    e.stopPropagation();
    if (!canCreate()) {
      toast.error('Assine um plano para duplicar orçamentos');
      navigate('/subscribe');
      return;
    }
    try {
      await api.post(`/proposals/${id}/duplicate`);
      toast.success('Orçamento duplicado!');
      setActiveFilter('nao_enviado');
      loadProposals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao duplicar orçamento');
    }
  };

  const deleteProposal = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Excluir este orçamento?')) return;
    try {
      await api.delete(`/proposals/${id}`);
      toast.success('Orçamento excluído');
      loadProposals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir orçamento');
    }
  };

  const countByStatus = (status) => proposals.filter(p => p.status === status).length;

  const filtered = proposals.filter(p => {
    const matchStatus = activeFilter === 'all' || p.status === activeFilter;
    const matchSearch = !search || [p.titulo, p.cliente_nome, p.num].some(f =>
      f?.toLowerCase().includes(search.toLowerCase())
    );
    return matchStatus && matchSearch;
  }).sort((a, b) => {
    const oi = STATUS_ORDER.indexOf(a.status);
    const oj = STATUS_ORDER.indexOf(b.status);
    if (oi !== oj) return oi - oj;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const StatusButton = ({ status }) => {
    const cfg = STATUS_CONFIG[status];
    const count = countByStatus(status);
    const isActive = activeFilter === status;
    return (
      <button
        data-testid={`filter-btn-${status}`}
        onClick={() => setActiveFilter(isActive ? 'all' : status)}
        style={{ fontFamily: 'Outfit, sans-serif' }}
        className={`
          relative font-bold py-3 px-4 rounded-xl text-white
          border-b-4 text-center min-w-[100px] flex-1
          transition-all duration-100
          ${cfg.btn}
          ${isActive ? 'translate-y-1 !shadow-none !border-b-0 mt-1 ring-2 ring-white/20' : 'hover:-translate-y-0.5'}
        `}
      >
        <div className="text-xs font-normal opacity-80 mb-0.5">{cfg.label}</div>
        <div className="text-2xl font-black">{count}</div>
      </button>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Orçamentos</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{proposals.length} orçamentos no total</p>
        </div>
        <button
          data-testid="new-proposal-btn"
          onClick={handleNewProposal}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">Novo orçamento</span>
          <span className="sm:hidden">Novo</span>
        </button>
      </div>

      {/* 3D Status Buttons */}
      <div className="flex gap-3 flex-wrap">
        {STATUS_ORDER.filter(s => s !== 'pago').map(status => (
          <StatusButton key={status} status={status} />
        ))}
        <StatusButton status="pago" />
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          data-testid="search-input"
          className="field-input pl-9"
          placeholder="Buscar por título, cliente ou número..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-zinc-600 text-5xl mb-4">📋</div>
          <p className="text-zinc-400 font-medium">Nenhum orçamento encontrado</p>
          <p className="text-zinc-600 text-sm mt-1">Crie seu primeiro orçamento clicando em "+ Novo"</p>
          <button onClick={handleNewProposal}
            className="mt-4 bg-orange-500 hover:bg-orange-400 text-white font-medium px-5 py-2 rounded-lg transition-colors text-sm">
            Criar orçamento
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.nao_enviado;
            return (
              <div
                key={p.id}
                data-testid={`proposal-item-${p.id}`}
                onClick={() => navigate(`/proposals/${p.id}`)}
                className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-xl p-4 cursor-pointer transition-all duration-150 hover:bg-zinc-800/50 group"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-zinc-500">#{p.num}</span>
                      {p.is_recibo && (
                        <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">RECIBO</span>
                      )}
                    </div>
                    <div className="font-medium text-white truncate">{p.titulo}</div>
                    {p.cliente_nome && (
                      <div className="text-sm text-zinc-500 mt-0.5 truncate">{p.cliente_nome}</div>
                    )}
                  </div>

                  <div className="text-right shrink-0">
                    <div className="font-mono font-semibold text-white">{brl(p.valor)}</div>
                    <div className="mt-1">
                      <span className={`badge ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} mr-1.5`} />
                        {cfg.label}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={(e) => duplicateProposal(e, p.id)} title="Duplicar"
                      className="p-1.5 text-zinc-500 hover:text-orange-400 hover:bg-zinc-800 rounded-lg transition-colors">
                      <Copy size={15} />
                    </button>
                    <button onClick={(e) => deleteProposal(e, p.id)} title="Excluir"
                      className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-zinc-800 rounded-lg transition-colors">
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <ChevronRight size={16} className="text-zinc-600 group-hover:text-zinc-400 shrink-0 transition-colors" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
